import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, TrendingUp, TrendingDown, Lock } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";

/**
 * Public, no-login-required ticker snapshot for the landing page.
 * Rate-limited to 5 lookups per IP per 24h (server enforces). At 5, we show a "Sign up" CTA.
 */
export default function LandingSandbox() {
  const [ticker, setTicker] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [status, setStatus] = useState({ used: 0, limit: 5, remaining: 5 });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/sandbox/status");
        setStatus(data);
      } catch {
        /* ignore */
      }
    })();
  }, []);

  const lookup = async (e) => {
    if (e) e.preventDefault();
    if (!ticker.trim() || loading) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.get("/sandbox/lookup", {
        params: { ticker: ticker.trim() },
      });
      setResult(data);
      setStatus(data.sandbox || status);
    } catch (e) {
      const s = e?.response?.status;
      const msg = e?.response?.data?.detail || "Lookup failed — try again";
      setError(msg);
      if (s === 429) {
        setStatus((cur) => ({ ...cur, remaining: 0 }));
      }
    } finally {
      setLoading(false);
    }
  };

  const s = result?.snapshot;
  const consolidated = s?.consolidated_fair_value;
  const current = s?.current_price;
  const marginPct =
    current && consolidated
      ? ((consolidated - current) / current) * 100
      : null;
  const upside = marginPct !== null && marginPct >= 0;

  const outOfLookups = status.remaining <= 0;

  return (
    <section
      className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-20"
      data-testid="landing-sandbox"
    >
      <div className="overline mb-3 text-moss">— Try it, no sign-up required</div>
      <h2 className="font-serif-display text-3xl sm:text-4xl text-bone-50 mb-3 font-medium">
        Look up any stock's fair value in seconds.
      </h2>
      <p className="text-sm text-bone-50/70 mb-6 max-w-2xl">
        Type an NSE ticker (e.g. <span className="font-mono">TCS</span>, <span className="font-mono">INFY.NS</span>) or a US ticker (<span className="font-mono">AAPL</span>). We consolidate fair value estimates from 6 sources and show you the margin of safety instantly.
      </p>

      <form
        onSubmit={lookup}
        className="flex flex-col sm:flex-row gap-2 mb-3"
        data-testid="sandbox-form"
      >
        <input
          value={ticker}
          onChange={(e) => setTicker(e.target.value)}
          placeholder="Ticker or company name…"
          disabled={loading || outOfLookups}
          className="flex-1 h-12 px-4 text-sm rounded-sm border border-white/20 bg-white/10 backdrop-blur text-bone-50 placeholder:text-bone-50/50 focus:outline-none focus:ring-1 focus:ring-moss disabled:opacity-50"
          data-testid="sandbox-input"
        />
        <Button
          type="submit"
          disabled={loading || outOfLookups || !ticker.trim()}
          className="h-12 rounded-sm bg-moss hover:bg-moss-hover text-bone-50 gap-2 px-6"
          data-testid="sandbox-submit"
        >
          <Search className="w-4 h-4" />
          {loading ? "Fetching…" : "Get fair value"}
        </Button>
      </form>

      <div className="flex items-center gap-3 text-xs text-bone-50/60 mb-6 font-mono">
        <span data-testid="sandbox-remaining">
          {status.remaining} of {status.limit} free lookups left today
        </span>
        <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden max-w-[200px]">
          <div
            className="h-full bg-moss transition-all"
            style={{ width: `${Math.max(0, (status.remaining / status.limit) * 100)}%` }}
          />
        </div>
      </div>

      {outOfLookups && (
        <div
          className="border border-amber_brand/30 bg-amber_brand-bg/10 rounded-sm p-5 mb-6"
          data-testid="sandbox-signup-cta"
        >
          <div className="flex items-start gap-3">
            <Lock className="w-5 h-5 text-amber_brand mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-serif-display text-lg text-bone-50 mb-1">
                That's your 5 for today.
              </div>
              <div className="text-sm text-bone-50/70 mb-3">
                Sign up free to get unlimited lookups, watchlists, projections, and physical
                gold + land purchase guides.
              </div>
              <Link to="/signup">
                <Button
                  className="rounded-sm bg-amber_brand hover:bg-amber_brand/90 text-stone_brand-dark h-10"
                  data-testid="sandbox-signup-btn"
                >
                  Create free account
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {error && !outOfLookups && (
        <div
          className="border border-terracotta/40 bg-terracotta/10 rounded-sm p-3 mb-6 text-sm text-bone-50"
          data-testid="sandbox-error"
        >
          {error}
        </div>
      )}

      {result && s && (
        <div
          className="bg-bone-50 border border-line rounded-sm p-6"
          data-testid="sandbox-result"
        >
          <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="font-serif-display text-2xl text-stone_brand-dark font-medium">
                {s.company_name || result.ticker}
              </div>
              <div className="font-mono text-xs text-stone_brand-light uppercase tracking-widest">
                {result.ticker}
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono text-xs text-stone_brand-light uppercase tracking-widest mb-1">
                Current price
              </div>
              <div className="font-serif-display text-2xl text-stone_brand-dark font-medium">
                {current ? `${s.currency_symbol || "₹"}${current.toLocaleString("en-IN")}` : "—"}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 py-4 border-t border-b border-line mb-4">
            <div>
              <div className="font-mono text-[0.65rem] text-stone_brand-light uppercase tracking-widest mb-1">
                Consolidated FV
              </div>
              <div className="font-serif-display text-xl text-stone_brand-dark font-medium">
                {consolidated
                  ? `${s.currency_symbol || "₹"}${consolidated.toLocaleString("en-IN")}`
                  : "—"}
              </div>
            </div>
            <div>
              <div className="font-mono text-[0.65rem] text-stone_brand-light uppercase tracking-widest mb-1">
                Margin of safety
              </div>
              <div
                className={
                  "font-serif-display text-xl font-medium inline-flex items-center gap-1 " +
                  (marginPct === null
                    ? "text-stone_brand-dark"
                    : upside
                      ? "text-moss"
                      : "text-terracotta")
                }
              >
                {marginPct !== null &&
                  (upside ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />)}
                {marginPct !== null ? `${marginPct.toFixed(1)}%` : "—"}
              </div>
            </div>
            <div>
              <div className="font-mono text-[0.65rem] text-stone_brand-light uppercase tracking-widest mb-1">
                Sources agreeing
              </div>
              <div className="font-serif-display text-xl text-stone_brand-dark font-medium">
                {(s.fair_value_sources || []).filter((x) => x.value).length}/6
              </div>
            </div>
          </div>

          <div className="text-xs text-stone_brand mb-3">
            Sources: {(s.fair_value_sources || [])
              .filter((x) => x.value)
              .map((x) => x.label)
              .join(" · ") || "—"}
          </div>

          <Link to="/signup">
            <Button
              className="w-full sm:w-auto rounded-sm bg-stone_brand-dark hover:bg-stone_brand text-bone-50 h-10 gap-1.5"
              data-testid="sandbox-see-more"
            >
              Sign up free to see full analysis
            </Button>
          </Link>
        </div>
      )}
    </section>
  );
}
